import { React, useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import { 
	Table, 
	Button, 
	Modal, 
	message, 
	Space, 
	Card, 
	Tabs, 
	Layout,
	Form,
	Input,
	DatePicker,
	TimePicker,
	Select,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import { ConfigProvider } from "antd";
import RentSanctuaryNavigation from "./navigation/RentSanctuaryNavigation";
import { isOfficeSanctuaryUser } from "./rentSanctuaryUtils";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

const SANCTUARY_OTHER_VALUE = "__OTHER__";

const ViewRentApplication = () => {
	const isMobile = useIsMobile();
	const [applications, setApplications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [editForm] = Form.useForm();
	const editSanctuaryChoice = Form.useWatch("sanctuary_room", editForm);
	const [sanctuaryRoomList, setSanctuaryRoomList] = useState([]);
	const [selectedApplication, setSelectedApplication] = useState(null);
	const [rejectReason, setRejectReason] = useState("");
	const [activeTab, setActiveTab] = useState("신청");
	const [approvedSanctuaryFilter, setApprovedSanctuaryFilter] = useState(undefined);
	const [approvedRentTeamFilter, setApprovedRentTeamFilter] = useState(undefined);
	const canManage = isOfficeSanctuaryUser();

	useEffect(() => {
		fetchApplications();
		getSanctuaryRoomList();
	}, []);

	const getSanctuaryRoomList = async () => {
		try {
			const response = await useAxios.get(
				"/utils/sanctuary/list/"
			);
			if (response.status === 200) {
				let sanctuaryRoomList = response.data;
				sanctuaryRoomList.sort((a, b) => {
					if (a.sanctuary_name === b.sanctuary_name) {
						return a.room_name.localeCompare(b.room_name);
					}
					return a.sanctuary_name.localeCompare(b.sanctuary_name);
				});
				setSanctuaryRoomList(sanctuaryRoomList);
			}
		} catch (error) {
			message.error("성전 목록을 불러오는데 실패했습니다.");
		}
	};

	const fetchApplications = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/rental/sanctuary/apply/"
			);
			if (response.status === 200) {
				console.log("받은 데이터:", response.data);
				setApplications(response.data);
				console.log("설정된 applications:", response.data);
			}
		} catch (error) {
			message.error("신청 목록을 불러오는데 실패했습니다.");
			console.error("에러:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleAccept = async (record) => {
		try {
			setLoading(true);
			const response = await useAxios.put(
				`/utils/rental/sanctuary/apply/${record.id}/`,
				{
					status: "승인",
				}
			);

			if (response.status === 200) {
				message.success("성전 대여가 승인되었습니다.");
				fetchApplications();
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"승인 처리 중 오류가 발생했습니다."
			);
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const handleReject = (record) => {
		setSelectedApplication(record);
		setModalVisible(true);
	};

	const handleRejectConfirm = async () => {
		if (!rejectReason) {
			message.error("거절 사유를 입력해주세요.");
			return;
		}

		try {
			setLoading(true);
			const response = await useAxios.put(
				`/utils/rental/sanctuary/apply/${selectedApplication.id}/`,
				{
					status: "거절",
					reject_reason: rejectReason,
				}
			);

			if (response.status === 200) {
				message.success("신청이 거절되었습니다.");
				setModalVisible(false);
				setRejectReason("");
				fetchApplications();
			}
		} catch (error) {
			message.error("거절 처리 중 오류가 발생했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (record) => {
		setSelectedApplication(record);
		setDeleteModalVisible(true);
	};

	const handleDeleteConfirm = async () => {
		try {
			setLoading(true);
			const response = await useAxios.put(
				`/utils/rental/sanctuary/apply/${selectedApplication.id}/`,
				{
					status: "거절",
					reject_reason: "예약 취소",
				}
			);

			if (response.status === 200) {
				message.success("예약이 취소되었습니다.");
				setDeleteModalVisible(false);
				fetchApplications();
			}
		} catch (error) {
			message.error("예약 취소 처리 중 오류가 발생했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = (record) => {
		setSelectedApplication(record);
		const startDateTime = dayjs(record.start_datetime);
		const endDateTime = dayjs(record.end_datetime);
		
		editForm.setFieldsValue({
			sanctuary_room:
				record.sanctuary_room?.id ??
				(record.custom_sanctuary_name
					? SANCTUARY_OTHER_VALUE
					: undefined),
			custom_sanctuary_name: record.sanctuary_room
				? ""
				: record.custom_sanctuary_name || "",
			date: startDateTime,
			startTime: startDateTime,
			endTime: endDateTime,
			rent_team: record.rent_team,
			rent_for: record.rent_for,
			representative: record.representative,
			representative_tel: record.representative_tel,
		});
		setEditModalVisible(true);
	};

	const handleEditConfirm = async () => {
		try {
			const values = await editForm.validateFields();
			
			// 시간 검증
			if (values.endTime <= values.startTime) {
				message.error("종료 시간은 시작 시간보다 크게 설정해주세요.");
				return;
			}

			setLoading(true);

			// 날짜와 시간 결합
			const startDateTime = dayjs(values.date)
				.hour(values.startTime.hour())
				.minute(values.startTime.minute());

			const endDateTime = dayjs(values.date)
				.hour(values.endTime.hour())
				.minute(values.endTime.minute());

			let sanctuary_room = values.sanctuary_room;
			let custom_sanctuary_name = "";
			if (sanctuary_room === SANCTUARY_OTHER_VALUE) {
				sanctuary_room = null;
				custom_sanctuary_name = (
					values.custom_sanctuary_name || ""
				).trim();
			}

			const formData = {
				sanctuary_room,
				custom_sanctuary_name,
				start_datetime: startDateTime.format("YYYY-MM-DD HH:mm:ss"),
				end_datetime: endDateTime.format("YYYY-MM-DD HH:mm:ss"),
				rent_team: values.rent_team,
				rent_for: values.rent_for,
				representative: values.representative,
				representative_tel: values.representative_tel,
				status: selectedApplication.status, // 상태 유지
			};

			const response = await useAxios.put(
				`/utils/rental/sanctuary/apply/${selectedApplication.id}/`,
				formData
			);

			if (response.status === 200) {
				message.success("예약이 수정되었습니다.");
				setEditModalVisible(false);
				editForm.resetFields();
				fetchApplications();
			}
		} catch (error) {
			if (error.response?.data?.error) {
				message.error(error.response.data.error);
			} else if (error.errorFields) {
				message.error("입력 정보를 확인해주세요.");
			} else {
				message.error("예약 수정 중 오류가 발생했습니다.");
			}
		} finally {
			setLoading(false);
		}
	};

	const getColumns = (status) => {
		const baseColumns = [
			{
				title: "성전",
				key: "sanctuary",
				render: (_, record) => {
					const sanctuaryName =
						record.sanctuary_name ||
						record.sanctuary_room?.sanctuary_name ||
						record.custom_sanctuary_name;
					const roomName = record.sanctuary_room?.room_name;

					if (sanctuaryName && roomName && roomName !== "-") {
						return `${sanctuaryName} ${roomName}`;
					}
					return sanctuaryName || "알 수 없음";
				},
			},
			{
				title: "대여 일시",
				key: "datetime",
				render: (_, record) => (
					<span>
						{dayjs(
							record.start_datetime
						).format(
							"YYYY-MM-DD HH:mm"
						)}{" "}
						~{" "}
						{dayjs(
							record.end_datetime
						).format("HH:mm")}
					</span>
				),
			},
			{
				title: "교구/기관명",
				dataIndex: "rent_team",
				key: "rent_team",
			},
			{
				title: "사용 목적",
				dataIndex: "rent_for",
				key: "rent_for",
			},
			{
				title: "신청자",
				dataIndex: "representative",
				key: "representative",
			},
			{
				title: "신청자 연락처",
				dataIndex: "representative_tel",
				key: "representative_tel",
			},
			{
				title: "신청일",
				dataIndex: "created_at",
				key: "created_at",
				render: (date) =>
					dayjs(date).format("YYYY-MM-DD HH:mm"),
			},
		];

		if (status === "거절") {
			baseColumns.push({
				title: "거절 사유",
				dataIndex: "reject_reason",
				key: "reject_reason",
			});

		}
		
		if (status === "승인") {
			baseColumns.push({
				title: "승인 일시",
				dataIndex: "approved_at",
				key: "approved_at",
				render: (date) =>
					date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "-",
			});
			if (canManage) {
				baseColumns.push({
					title: "관리",
					key: "action",
					render: (_, record) => (
						<Space size="middle">
							<Button
								type="primary"
								onClick={() => handleEdit(record)}>
								수정
							</Button>
							<Button
								danger
								onClick={() => handleDelete(record)}>
								예약 취소
							</Button>
						</Space>
					),
				});
			}
		}

		if (status === "신청" && canManage) {
			baseColumns.push({
				title: "관리",
				key: "action",
				render: (_, record) => (
					<Space size="middle">
						<Button
							type="primary"
							onClick={() =>
								handleAccept(
									record
								)
							}>
							승인
						</Button>
						<Button
							danger
							onClick={() =>
								handleReject(
									record
								)
							}>
							거절
						</Button>
					</Space>
				),
			});
		}

		return baseColumns;
	};

	const approvedApplications = applications.filter(
		(app) => app.status === "승인"
	);

	const approvedSanctuaryOptions = [
		...new Set(
			approvedApplications
				.map(
					(app) =>
						app.sanctuary_name ||
						app.sanctuary_room?.sanctuary_name ||
						app.custom_sanctuary_name,
				)
				.filter(Boolean)
		),
	];

	const approvedRentTeamOptions = [
		...new Set(
			approvedApplications
				.map((app) => app.rent_team)
				.filter(Boolean)
		),
	];

	const filteredApprovedApplications = approvedApplications.filter((app) => {
		const sanctuaryName =
			app.sanctuary_name ||
			app.sanctuary_room?.sanctuary_name ||
			app.custom_sanctuary_name;
		const isSanctuaryMatched =
			!approvedSanctuaryFilter ||
			sanctuaryName === approvedSanctuaryFilter;
		const isRentTeamMatched =
			!approvedRentTeamFilter ||
			app.rent_team === approvedRentTeamFilter;

		return isSanctuaryMatched && isRentTeamMatched;
	});

	const items = [
		{
			key: "신청",
			label: "신청",
			children: (
				<Table
					columns={getColumns("신청")}
					dataSource={applications.filter((app) => app.status === "신청")}
					rowKey="id"
					loading={loading}
					pagination={{ pageSize: 10 }}
					sticky={{ offsetHeader: 64 }}
				/>
			),
		},
		{
			key: "승인",
			label: "승인",
			children: (
				<>
					<Space
						size="middle"
						wrap
						style={{ marginBottom: 16 }}>
						<Select
							allowClear
							style={{
								minWidth: isMobile ? 0 : 180,
								width: isMobile ? "100%" : undefined,
							}}
							placeholder="성전 선택"
							value={approvedSanctuaryFilter}
							onChange={(value) =>
								setApprovedSanctuaryFilter(value)
							}>
							{approvedSanctuaryOptions.map(
								(sanctuaryName) => (
									<Option
										key={sanctuaryName}
										value={sanctuaryName}>
										{sanctuaryName}
									</Option>
								)
							)}
						</Select>
						<Select
							allowClear
							style={{
								minWidth: isMobile ? 0 : 220,
								width: isMobile ? "100%" : undefined,
							}}
							placeholder="교구/기관 선택"
							value={approvedRentTeamFilter}
							onChange={(value) =>
								setApprovedRentTeamFilter(value)
							}>
							{approvedRentTeamOptions.map((rentTeam) => (
								<Option
									key={rentTeam}
									value={rentTeam}>
									{rentTeam}
								</Option>
							))}
						</Select>
					</Space>
					<Table
						columns={getColumns("승인")}
						dataSource={filteredApprovedApplications}
						rowKey="id"
						loading={loading}
						pagination={{ pageSize: 10 }}
						sticky={{ offsetHeader: 64 }}
					/>
				</>
			),
		},
		{
			key: "거절",
			label: "거절",
			children: (
				<Table
					columns={getColumns("거절")}
					dataSource={applications.filter((app) => app.status === "거절")}
					rowKey="id"
					loading={loading}
					pagination={{ pageSize: 10 }}
					sticky={{ offsetHeader: 64 }}
				/>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="성전 대여 신청 관리" />
			<Layout className="member-layout">
				<RentSanctuaryNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container page">
							<PageHeader title="성전 대여 신청 관리" />
							<div className="utils-rent_sanctuary__content-container">
								<Card className="surface-card utils-rent_sanctuary__table-card">
									<Tabs
										activeKey={activeTab}
										onChange={setActiveTab}
										items={items}
									/>
								</Card>
							</div>
						</div>

						<Modal
							title="신청 거절"
							open={modalVisible}
							onOk={handleRejectConfirm}
							onCancel={() => {
								setModalVisible(false);
								setRejectReason("");
							}}
							okText="거절"
							cancelText="취소"
							okButtonProps={{ danger: true }}>
							<p>거절 사유를 입력해주세요:</p>
							<textarea
								value={rejectReason}
								onChange={(e) =>
									setRejectReason(e.target.value)
								}
								style={{
									width: "100%",
									minHeight: "100px",
									padding: "8px",
								}}
								placeholder="거절 사유를 입력하세요"
							/>
						</Modal>

						<Modal
							title="예약 취소 확인"
							open={deleteModalVisible}
							onOk={handleDeleteConfirm}
							onCancel={() => {
								setDeleteModalVisible(false);
							}}
							okText="예약 취소"
							cancelText="취소"
							okButtonProps={{ danger: true }}>
							<p>정말로 이 예약을 취소하시겠습니까?</p>
							{selectedApplication && (
								<div style={{ marginTop: "16px" }}>
									<p><strong>성전:</strong>{" "}
										{selectedApplication.sanctuary_name ||
											selectedApplication.sanctuary_room
												?.sanctuary_name ||
											selectedApplication.custom_sanctuary_name ||
											"알 수 없음"}
										{selectedApplication.sanctuary_room
											?.room_name &&
											selectedApplication.sanctuary_room
												.room_name !== "-"
											? ` ${selectedApplication.sanctuary_room.room_name}`
											: ""}
									</p>
									<p><strong>대여 일시:</strong> {
										dayjs(selectedApplication.start_datetime).format("YYYY-MM-DD HH:mm")
									} ~ {dayjs(selectedApplication.end_datetime).format("HH:mm")}</p>
									<p><strong>교구/기관명:</strong> {selectedApplication.rent_team}</p>
								</div>
							)}
							<p style={{ marginTop: "16px", color: "#ff4d4f" }}>
								예약 취소 시 상태가 '거절'로 변경되고, 사유는 '예약 취소'로 기록됩니다.
							</p>
						</Modal>

						<Modal
							title="예약 수정"
							open={editModalVisible}
							onOk={handleEditConfirm}
							onCancel={() => {
								setEditModalVisible(false);
								editForm.resetFields();
							}}
							okText="수정"
							cancelText="취소"
							width={600}
							okButtonProps={{ loading: loading }}>
							<Form
								form={editForm}
								layout="vertical">
								<Form.Item
									name="sanctuary_room"
									label="성전 선택"
									rules={[
										{
											required: true,
											message: "성전을 선택해주세요",
										},
									]}>
									<Select placeholder="성전을 선택하세요">
										{sanctuaryRoomList.map((sanctuaryRoom) => (
											<Option
												key={sanctuaryRoom.id}
												value={sanctuaryRoom.id}>
												{sanctuaryRoom.room_name === "-"
													? sanctuaryRoom.sanctuary_name
													: `${sanctuaryRoom.sanctuary_name} ${sanctuaryRoom.room_name}`}
											</Option>
										))}
										<Option
											key="__OTHER__"
											value={SANCTUARY_OTHER_VALUE}>
											기타
										</Option>
									</Select>
								</Form.Item>

								{editSanctuaryChoice ===
									SANCTUARY_OTHER_VALUE && (
									<Form.Item
										name="custom_sanctuary_name"
										label="성전 이름"
										rules={[
											{
												required: true,
												message:
													"성전 이름을 입력해주세요",
											},
											{
												validator(_, value) {
													if (
														!(
															value &&
															String(
																value,
															).trim()
														)
													) {
														return Promise.reject(
															new Error(
																"성전 이름을 입력해주세요",
															),
														);
													}
													return Promise.resolve();
												},
											},
										]}>
										<Input placeholder="신청할 성전 이름을 입력하세요" />
									</Form.Item>
								)}

								<Form.Item
									name="date"
									label="날짜"
									rules={[
										{
											required: true,
											message: "날짜를 선택해주세요",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{ width: "100%" }}
										format="YYYY-MM-DD"
										allowClear={false}
									/>
								</Form.Item>

								<Form.Item
									name="startTime"
									label="시작 시간"
									rules={[
										{
											required: true,
											message: "시작 시간을 선택해주세요",
										},
									]}>
									<TimePicker
										inputReadOnly={isMobile}
										style={{ width: "100%" }}
										format="HH:mm"
										minuteStep={30}
										allowClear={false}
									/>
								</Form.Item>

								<Form.Item
									name="endTime"
									label="종료 시간"
									rules={[
										{
											required: true,
											message: "종료 시간을 선택해주세요",
										},
									]}>
									<TimePicker
										inputReadOnly={isMobile}
										style={{ width: "100%" }}
										format="HH:mm"
										minuteStep={30}
										allowClear={false}
									/>
								</Form.Item>

								<Form.Item
									name="rent_team"
									label="교구/기관명"
									rules={[
										{
											required: true,
											message: "교구/기관명을 입력해주세요",
										},
									]}>
									<Input placeholder="교구/기관명을 입력하세요" />
								</Form.Item>

								<Form.Item
									name="rent_for"
									label="사용 목적"
									rules={[
										{
											required: true,
											message: "사용 목적을 입력해주세요",
										},
									]}>
									<TextArea
										rows={4}
										placeholder="사용 목적을 입력하세요"
									/>
								</Form.Item>

								<Form.Item
									name="representative"
									label="신청자 이름"
									rules={[
										{
											required: true,
											message: "신청자 이름을 입력해주세요",
										},
									]}>
									<Input placeholder="신청자 이름을 입력하세요" />
								</Form.Item>

								<Form.Item
									name="representative_tel"
									label="신청자 연락처"
									rules={[
										{
											required: true,
											message: "신청자 연락처를 입력해주세요",
										},
										{
											pattern: /^[0-9-]+$/,
											message: "숫자와 하이픈(-)만 입력 가능합니다",
										},
									]}>
									<Input placeholder="연락처를 입력하세요 (예: 010-1234-5678)" />
								</Form.Item>
							</Form>
						</Modal>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ViewRentApplication;
